import React, { useState } from 'react';
import { FormElement, ContentRun } from '../store/types';
import { useAppDispatch, useAppSelector } from '../../../../../ReaduxToolkit/Hooks';
import { setActiveCell, setSelection } from '../store/formDesignerUiSlice';
import { updateElement } from '../store/formDocumentSlice';

interface Props {
  element: FormElement;
  isSelected: boolean;
  pageId?: string;
  onMouseDown: (e: React.MouseEvent) => void;
}

function RunsView({
  runs,
  onToggle,
}: {
  runs: ContentRun[] | undefined;
  onToggle?: (index: number, checked: boolean) => void;
}) {
  if (!runs || runs.length === 0) return null;
  return (
    <>
      {runs.map((run, i) => {
        if (run.type === 'variable') {
          return (
            <React.Fragment key={i}>
              ${run.fieldKey}$
            </React.Fragment>
          );
        } else if (run.type === 'textbox') {
          const placeholderVal = run.fieldKey ? `$${run.fieldKey}$` : (run.value || '');
          return (
            <input
              key={i}
              type="text"
              style={{
                width: '100%',
                border: '1px solid #c0c0c0',
                padding: '2px 4px',
                borderRadius: '3px',
                boxSizing: 'border-box',
                fontSize: '11px',
                background: '#fff'
              }}
              placeholder={placeholderVal}
              readOnly
            />
          );
        } else if (run.type === 'textarea') {
          const val = run.fieldKey ? `$${run.fieldKey}$` : (run.value || '');
          return (
            <textarea
              key={i}
              style={{
                width: '100%',
                border: '1px solid #c0c0c0',
                padding: '2px 4px',
                borderRadius: '3px',
                boxSizing: 'border-box',
                fontSize: '11px',
                background: '#fff',
                resize: 'none',
                height: '40px'
              }}
              value={val}
              readOnly
            />
          );
        } else if (run.type === 'checkbox') {
          return (
            <label
              key={i}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.15em', margin: 0, fontSize: '11px', cursor: onToggle ? 'pointer' : 'default' }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <input type="checkbox" style={{ width: '0.75em', height: '0.75em', margin: 0, flexShrink: 0 }} checked={!!run.checked} onChange={(e) => onToggle?.(i, e.target.checked)} />
              {run.value || 'Checkbox'}
            </label>
          );
        } else if (run.type === 'radio') {
          return (
            <label
              key={i}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.15em', margin: 0, fontSize: '11px', cursor: onToggle ? 'pointer' : 'default' }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <input type="radio" style={{ width: '0.75em', height: '0.75em', margin: 0, flexShrink: 0 }} checked={!!run.checked} onChange={(e) => onToggle?.(i, e.target.checked)} />
              {run.value || 'Option'}
            </label>
          );
        } else {
          return <React.Fragment key={i}>{run.value}</React.Fragment>;
        }
      })}
    </>
  );
}

// Inputs the PDF importer adds for blank spaces have no border or fill of their own, so they were
// invisible in the designer. Give them a faint dashed frame so every input can be seen and picked.
function blankInputFrame(s: FormElement['style'], fallbackBorder: string): { border: string; background: string } {
  const unstyled = !s.borderStyle || s.borderStyle === 'none' || !s.borderWidth;
  const transparent = !s.background || s.background === 'transparent';
  return {
    border: unstyled ? '1px dashed #8fa8c8' : fallbackBorder,
    background: transparent ? (unstyled ? 'rgba(79,124,255,0.07)' : '#ffffff') : (s.background as string),
  };
}

function elementBodyStyle(el: FormElement): React.CSSProperties {
  const s = el.style;
  const fontSz = typeof s.fontSize === 'number' ? `${s.fontSize}px` : (s.fontSize || '12px');
  return {
    fontFamily: s.fontFamily || 'inherit',
    fontSize: fontSz,
    fontWeight: s.fontWeight || 'normal',
    fontStyle: s.fontStyle || 'normal',
    textDecoration: s.textDecoration || 'none',
    color: s.color || '#000000',
    textAlign: s.textAlign || 'left',
    lineHeight: s.lineHeight || 1.15,
    letterSpacing: s.letterSpacing || 'normal',
  };
}

export default function ElementRenderer({ element, isSelected, pageId, onMouseDown }: Props) {
  const dispatch = useAppDispatch();
  const s = element.style;

  const isInputField = element.type === 'textbox' || element.type === 'textarea' || element.type === 'date';

  const handleRunToggle = (index: number, checked: boolean) => {
    if (!pageId || !element.content) return;
    const newContent = element.content.map((r, i) => (i === index ? { ...r, checked } : r));
    dispatch(updateElement({ pageId, id: element.id, changes: { content: newContent } }));
  };

  const wrapperStyle: React.CSSProperties = {
    position: 'absolute',
    left: element.x,
    top: element.y,
    width: element.width,
    height: element.height,
    transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
    background: s.background && s.background !== 'transparent' ? s.background : undefined,
    borderWidth: isInputField ? 0 : (s.borderWidth || 0),
    borderStyle: isInputField ? undefined : (s.borderStyle && s.borderStyle !== 'none' ? s.borderStyle : undefined),
    borderColor: isInputField ? undefined : s.borderColor,
    borderRadius: s.borderRadius,
    padding: s.padding ?? 0,
    boxSizing: 'border-box',
    display: element.visible ? undefined : 'none',
    cursor: element.locked ? 'default' : (isInputField || element.type === 'checkbox' || element.type === 'radio' ? 'pointer' : 'move'),
    overflow: (element.type === 'text' || element.type === 'label' || element.type === 'heading' || element.type === 'rect' || element.type === 'checkbox') ? 'visible' : 'hidden',
    userSelect: 'none',
    zIndex: isInputField || element.type === 'checkbox' || element.type === 'radio' ? 10 : (element.type === 'rect' ? 1 : 2),
    pointerEvents: element.locked && (element.type === 'rect' || element.type === 'hline' || element.type === 'vline') ? 'none' : 'auto',
  };

  let body: React.ReactNode;
  switch (element.type) {
    case 'heading':
    case 'label':
    case 'pagenumber':
      body = (
        <div style={{ ...elementBodyStyle(element), width: '100%', height: '100%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'flex-start', lineHeight: 1.15 }}>
          <RunsView runs={element.content} onToggle={handleRunToggle} />
        </div>
      );
      break;
    case 'paragraph':
      body = (
        <div style={{ ...elementBodyStyle(element), width: '100%', height: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'flex', alignItems: 'flex-start', lineHeight: 1.15 }}>
          <RunsView runs={element.content} onToggle={handleRunToggle} />
        </div>
      );
      break;
    case 'text':
      body = (
        <div style={{ ...elementBodyStyle(element), width: '100%', height: '100%', whiteSpace: 'nowrap', display: 'flex', alignItems: 'flex-start', lineHeight: 1.15 }}>
          <RunsView runs={element.content} onToggle={handleRunToggle} />
        </div>
      );
      break;
    case 'hline':
      body = <div style={{ width: '100%', height: 0, borderTop: `${s.borderWidth || 1}px ${s.borderStyle || 'solid'} ${s.borderColor || '#000'}`, pointerEvents: 'none' }} />;
      break;
    case 'vline':
      body = <div style={{ width: 0, height: '100%', borderLeft: `${s.borderWidth || 1}px ${s.borderStyle || 'solid'} ${s.borderColor || '#000'}`, pointerEvents: 'none' }} />;
      break;
    case 'rect':
      body = null;
      break;
    case 'checkbox':
      body = (
        <label style={{ ...elementBodyStyle(element), display: 'flex', alignItems: 'center', gap: '0.15em', height: '100%', margin: 0, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          <input
            type="checkbox"
            style={{ width: '0.75em', height: '0.75em', margin: 0, flexShrink: 0 }}
            checked={!!element.typeProps?.checkbox?.checked}
            onChange={(e) => {
              if (!pageId) return;
              dispatch(
                updateElement({
                  pageId,
                  id: element.id,
                  changes: {
                    typeProps: {
                      ...element.typeProps,
                      checkbox: { ...element.typeProps?.checkbox, label: element.typeProps?.checkbox?.label || '', checked: e.target.checked },
                    },
                  },
                })
              );
            }}
            onMouseDown={(e) => e.stopPropagation()}
          />
          {element.typeProps?.checkbox?.label}
        </label>
      );
      break;
    case 'radio':
      body = (
        <label style={{ ...elementBodyStyle(element), display: 'flex', alignItems: 'center', gap: '0.15em', height: '100%', margin: 0, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          <input
            type="radio"
            style={{ width: '0.75em', height: '0.75em', margin: 0, flexShrink: 0 }}
            checked={!!element.typeProps?.radio?.checked}
            onChange={(e) => {
              if (!pageId) return;
              dispatch(
                updateElement({
                  pageId,
                  id: element.id,
                  changes: {
                    typeProps: {
                      ...element.typeProps,
                      radio: { ...element.typeProps?.radio, label: element.typeProps?.radio?.label || '', checked: e.target.checked, groupName: element.typeProps?.radio?.groupName || '' },
                    },
                  },
                })
              );
            }}
            onMouseDown={(e) => e.stopPropagation()}
          />
          {element.typeProps?.radio?.label}
        </label>
      );
      break;
    case 'dropdown':
      body = (
        <select style={{ ...elementBodyStyle(element), width: '100%', height: '100%' }} disabled>
          {(element.typeProps?.dropdown?.options || []).map((o, i) => (
            <option key={i}>{o}</option>
          ))}
        </select>
      );
      break;
    case 'textbox': {
      const hasBinding = Boolean(element.bindings?.dynamicField);
      const value = element.bindings?.defaultValue ?? '';
      const placeholder = hasBinding ? `$${element.bindings?.dynamicField}$` : '';
      const displayVal = hasBinding && !value ? placeholder : value;
      body = (
        <div
          style={{
            ...elementBodyStyle(element),
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '0 3px',
            boxSizing: 'border-box',
            ...blankInputFrame(s, `${s.borderWidth || 1}px ${s.borderStyle || 'solid'} ${s.borderColor || '#cbd5e1'}`),
            borderRadius: s.borderRadius || 3,
          }}
        >
          <input
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: s.fontSize ? `${s.fontSize}px` : (element.height <= 14 ? '8px' : (element.height <= 18 ? '9.5px' : '11px')),
              fontFamily: s.fontFamily || 'inherit',
              fontWeight: hasBinding ? 'bold' : (s.fontWeight || 'normal'),
              fontStyle: s.fontStyle || 'normal',
              color: hasBinding ? '#0d6efd' : (s.color || '#000000'),
              padding: 0,
              margin: 0,
            }}
            value={displayVal}
            placeholder={placeholder}
            readOnly={hasBinding}
            onChange={(e) => {
              if (hasBinding || !pageId) return;
              dispatch(
                updateElement({
                  pageId,
                  id: element.id,
                  changes: {
                    bindings: { ...element.bindings, defaultValue: e.target.value },
                  },
                })
              );
            }}
            onFocus={() => {
              if (pageId && !isSelected) {
                dispatch(setSelection([element.id]));
              }
            }}
          />
        </div>
      );
      break;
    }
    case 'textarea': {
      const hasBinding = Boolean(element.bindings?.dynamicField);
      const value = element.bindings?.defaultValue ?? '';
      const placeholder = hasBinding ? `$${element.bindings?.dynamicField}$` : '';
      body = (
        <textarea
          style={{
            ...elementBodyStyle(element),
            width: '100%',
            height: '100%',
            ...blankInputFrame(s, `${s.borderWidth || 1}px ${s.borderStyle || 'solid'} ${s.borderColor || '#c0c0c0'}`),
            resize: 'none',
            padding: '2px 4px',
            boxSizing: 'border-box',
          }}
          value={hasBinding && !value ? placeholder : value}
          placeholder={placeholder}
          readOnly={hasBinding}
          onChange={(e) => {
            if (hasBinding || !pageId) return;
            dispatch(
              updateElement({
                pageId,
                id: element.id,
                changes: {
                  bindings: { ...element.bindings, defaultValue: e.target.value },
                },
              })
            );
          }}
          onMouseDown={(e) => e.stopPropagation()}
        />
      );
      break;
    }
    case 'date': {
      const hasBinding = Boolean(element.bindings?.dynamicField);
      const value = element.bindings?.defaultValue ?? '';
      const placeholder = hasBinding ? `$${element.bindings?.dynamicField}$` : 'MM/DD/YYYY';
      body = (
        <div
          style={{
            ...elementBodyStyle(element),
            width: '100%',
            height: '100%',
            border: `${s.borderWidth || 1}px ${s.borderStyle || 'solid'} ${s.borderColor || '#000000'}`,
            display: 'flex',
            alignItems: 'center',
            padding: '0 4px',
            boxSizing: 'border-box',
            background: s.background && s.background !== 'transparent' ? s.background : '#ffffff',
          }}
        >
          <input
            style={{
              width: '100%',
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: s.fontSize ? `${s.fontSize}px` : 'inherit',
              fontFamily: s.fontFamily || 'inherit',
              color: s.color || '#000000',
              padding: 0,
              margin: 0,
            }}
            value={hasBinding && !value ? placeholder : value}
            placeholder={placeholder}
            readOnly={hasBinding}
            onChange={(e) => {
              if (hasBinding || !pageId) return;
              dispatch(
                updateElement({
                  pageId,
                  id: element.id,
                  changes: {
                    bindings: { ...element.bindings, defaultValue: e.target.value },
                  },
                })
              );
            }}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </div>
      );
      break;
    }
    case 'signature':
      body = (
        <div style={{ ...elementBodyStyle(element), width: '100%', height: '100%', borderBottom: '1px solid #000', display: 'flex', alignItems: 'flex-end', color: '#999' }}>
          {element.typeProps?.signature?.placeholderText || 'Signature'}
        </div>
      );
      break;
    case 'image':
      body = <ImageBody element={element} />;
      break;
    case 'table':
      body = <TableBody element={element} isSelected={isSelected} pageId={pageId} />;
      break;
    default:
      body = null;
  }

  return (
    <div
      className={`fd-element${isSelected ? ' fd-element-selected' : ''}${(isInputField || element.type === 'checkbox' || element.type === 'radio') && element.bindings?.dynamicField ? ' fd-element-bound' : ''}${element.locked ? ' fd-element-locked' : ''}`}
      data-element-id={element.id}
      data-locked={element.locked ? 'true' : 'false'}
      title={element.bindings?.dynamicField ? `Field: ${element.bindings.dynamicField}` : undefined}
      style={wrapperStyle}
      onMouseDown={onMouseDown}
    >
      {body}
    </div>
  );
}

function TableBody({ element, isSelected, pageId }: { element: FormElement; isSelected: boolean; pageId?: string }) {
  const dispatch = useAppDispatch();
  const activeCell = useAppSelector((s) => s.FormDesignerUi.activeCell);
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);
  const t = element.typeProps?.table;
  if (!t) return null;

  const handleCellRunToggle = (r: number, c: number, index: number, checked: boolean) => {
    if (!pageId) return;
    const newCells = t.cells.map((row, ri) =>
      ri === r
        ? row.map((cell, ci) => (ci === c ? cell.map((run, ri2) => (ri2 === index ? { ...run, checked } : run)) : cell))
        : row
    );
    dispatch(
      updateElement({
        pageId,
        id: element.id,
        changes: { typeProps: { ...element.typeProps, table: { ...t, cells: newCells } } },
      })
    );
  };
  const covered = new Set<string>();
  t.merges.forEach((m) => {
    for (let r = m.r; r < m.r + m.rowSpan; r++) {
      for (let c = m.c; c < m.c + m.colSpan; c++) {
        if (r === m.r && c === m.c) continue;
        covered.add(`${r}_${c}`);
      }
    }
  });

  return (
    <table style={{ width: '100%', height: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <tbody>
        {Array.from({ length: t.rows }, (_, r) => (
          <tr key={r} style={{ height: t.rowHeights[r] || 'auto' }}>
            {Array.from({ length: t.cols }, (_, c) => {
              const key = `${r}_${c}`;
              if (covered.has(key)) return null;
              const merge = t.merges.find((m) => m.r === r && m.c === c);
              const isActive = isSelected && activeCell?.elementId === element.id && activeCell.row === r && activeCell.col === c;
              const cellKey = `${r}_${c}`;
              const isDragOver = dragOverCell === cellKey;
              return (
                <td
                  key={c}
                  data-fd-cell-row={r}
                  data-fd-cell-col={c}
                  rowSpan={merge?.rowSpan || 1}
                  colSpan={merge?.colSpan || 1}
                  onClick={(e) => {
                    if (!isSelected) return;
                    e.stopPropagation();
                    dispatch(setActiveCell({ elementId: element.id, row: r, col: c }));
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setDragOverCell(cellKey);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDragLeave={() => setDragOverCell((cur) => (cur === cellKey ? null : cur))}
                  onDrop={() => setDragOverCell(null)}
                  style={{
                    border: isActive ? '2px solid #4f7cff' : isDragOver ? '2px dashed #4f7cff' : '1px solid #999',
                    background: isDragOver ? 'rgba(79,124,255,0.12)' : undefined,
                    padding: 4,
                    width: `${t.colWidths[c] || 100 / t.cols}%`,
                    verticalAlign: 'middle',
                    fontSize: 12,
                    cursor: isSelected ? 'text' : undefined,
                  }}
                >
                  <RunsView runs={t.cells?.[r]?.[c]} onToggle={(idx, checked) => handleCellRunToggle(r, c, idx, checked)} />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ImageBody({ element }: { element: FormElement }) {
  const [failed, setFailed] = useState(false);
  const src = element.typeProps?.image?.src;

  if (!src || failed) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 12, background: '#f8f9fb', border: '1px dashed #ccc', boxSizing: 'border-box' }}>
        {failed ? 'Image unavailable' : 'Image'}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      onError={() => setFailed(true)}
      style={{ width: '100%', height: '100%', objectFit: element.typeProps?.image?.fit || 'contain' }}
    />
  );
}
